import type { AudioCapture, MicrophoneDevice } from "./types";
import { PCM_TAP_PROCESSOR, PCM_TAP_SOURCE } from "./worklet";

export class MicrophoneError extends Error {
  constructor(
    readonly reason: "mic_denied" | "mic_unavailable",
    message: string,
  ) {
    super(message);
  }
}

type MediaDevicesLike = Pick<MediaDevices, "enumerateDevices" | "getUserMedia">;

function mediaDevices(): MediaDevicesLike | undefined {
  return typeof navigator === "undefined" ? undefined : navigator.mediaDevices;
}

function deniedOrUnavailable(error: unknown): MicrophoneError {
  const name = error instanceof Error ? error.name : "";
  if (name === "NotAllowedError" || name === "SecurityError")
    return new MicrophoneError("mic_denied", "Microphone access was blocked");
  return new MicrophoneError("mic_unavailable", "No microphone is available");
}

/**
 * Audio inputs the browser will let the page use. Labels are hidden until the page has held a
 * microphone once, so an unlabeled list triggers one permission request before enumerating again.
 */
export async function listMicrophones(
  devices: MediaDevicesLike | undefined = mediaDevices(),
): Promise<MicrophoneDevice[]> {
  if (!devices?.enumerateDevices) return [];
  const inputs = (await devices.enumerateDevices()).filter(
    (d) => d.kind === "audioinput" && d.deviceId,
  );
  let labeled = inputs;
  if (inputs.length && inputs.every((d) => !d.label)) {
    try {
      const stream = await devices.getUserMedia({ audio: true });
      for (const track of stream.getTracks()) track.stop();
      labeled = (await devices.enumerateDevices()).filter(
        (d) => d.kind === "audioinput" && d.deviceId,
      );
    } catch {
      // Permission refused: fall through with placeholder labels.
    }
  }
  return labeled.map((d, index) => ({
    deviceId: d.deviceId,
    label: d.label || `Microphone ${index + 1}`,
  }));
}

/**
 * Opens a microphone and taps it through an AudioWorklet that emits 16-bit PCM.
 * Throws `MicrophoneError` so the session can tell a denied permission from a missing device.
 * A remembered `deviceId` that no longer exists falls back to the system default.
 */
export async function browserMicrophone(
  options: { deviceId?: string } = {},
): Promise<AudioCapture> {
  const devices = mediaDevices();
  if (!devices?.getUserMedia)
    throw new MicrophoneError("mic_unavailable", "Microphone capture is not supported here");
  const base: MediaTrackConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    channelCount: 1,
  };
  let stream: MediaStream;
  try {
    stream = await devices.getUserMedia({
      audio: options.deviceId ? { ...base, deviceId: { exact: options.deviceId } } : base,
    });
  } catch (error) {
    const name = error instanceof Error ? error.name : "";
    if (!options.deviceId || (name !== "OverconstrainedError" && name !== "NotFoundError"))
      throw deniedOrUnavailable(error);
    try {
      stream = await devices.getUserMedia({ audio: base });
    } catch (fallbackError) {
      throw deniedOrUnavailable(fallbackError);
    }
  }
  const context = new AudioContext();
  let node: AudioWorkletNode;
  const moduleUrl = URL.createObjectURL(
    new Blob([PCM_TAP_SOURCE], { type: "application/javascript" }),
  );
  try {
    await context.audioWorklet.addModule(moduleUrl);
    node = new AudioWorkletNode(context, PCM_TAP_PROCESSOR, {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      channelCount: 1,
    });
  } catch (error) {
    for (const track of stream.getTracks()) track.stop();
    await context.close();
    throw new MicrophoneError(
      "mic_unavailable",
      error instanceof Error ? error.message : "Audio processing is unavailable",
    );
  } finally {
    URL.revokeObjectURL(moduleUrl);
  }
  context.createMediaStreamSource(stream).connect(node);
  if (context.state === "suspended") await context.resume();
  let closed = false;
  return {
    sampleRate: context.sampleRate,
    onChunk(listener) {
      node.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
        if (!closed) listener(event.data);
      };
    },
    async close() {
      if (closed) return;
      closed = true;
      node.port.postMessage("stop");
      node.port.onmessage = null;
      for (const track of stream.getTracks()) track.stop();
      node.disconnect();
      if (context.state !== "closed") await context.close();
    },
  };
}
